import React, { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { authService } from '../features/user/userService';
import { logout, updateUser } from '../features/user/userSlice';
import { base_url } from '../utils/axiosConfig';
import axios from 'axios';
import {
  FaUser, FaBoxOpen, FaTruck, FaLock, FaSignOutAlt,
  FaShieldAlt, FaChevronRight, FaSave, FaEye, FaEyeSlash,
  FaSearch, FaMapMarkerAlt, FaCheckCircle, FaTimesCircle,
  FaTrashAlt, FaClock, FaEnvelope, FaPhone,
} from 'react-icons/fa';
import { MdRepeat } from 'react-icons/md';
import { BsFillCartCheckFill } from 'react-icons/bs';

/* ── Menu items ─────────────────────────────────────────── */
const MENU = [
  { id: 'profile',  label: 'My Profile',       icon: <FaUser />,     path: '/profile' },
  { id: 'orders',   label: 'My Orders',         icon: <BsFillCartCheckFill />, path: '/orders' },
  { id: 'track',    label: 'Track Order',        icon: <FaTruck />,    path: '/track' },
  { id: 'password', label: 'Change Password',   icon: <FaLock />,     path: '/password' },
  { id: 'account',  label: 'Account Settings',  icon: <FaShieldAlt />, path: '/account' },
];

/* ── Status colors ──────────────────────────────────────── */
const statusStyle = (status) => {
  const map = {
    Delivered:  { color: '#10b981', bg: '#d1fae5' },
    Cancelled:  { color: '#ef4444', bg: '#fee2e2' },
    Return:     { color: '#f97316', bg: '#ffedd5' },
    Shipped:    { color: '#8b5cf6', bg: '#ede9fe' },
    Processing: { color: '#3b82f6', bg: '#dbeafe' },
    Pending:    { color: '#f59e0b', bg: '#fef3c7' },
  };
  return map[status] || { color: '#6b7280', bg: '#f3f4f6' };
};

/* ── Track Order stepper data ───────────────────────────── */
const TRACK_STAGES = ['Ordered', 'In Transit', 'Out for Delivery', 'Delivered'];

const TRACK_STAGE_ICONS = {
  'Ordered':          '🏪',
  'In Transit':       '🚚',
  'Out for Delivery': '🛵',
  'Delivered':        '✅',
};

const getActiveStepIndex = (status) => {
  const idx = TRACK_STAGES.indexOf(status);
  return idx === -1 ? 0 : idx;
};

const calcProgress = (status) => {
  const idx = getActiveStepIndex(status);
  return ((idx + 1) / TRACK_STAGES.length) * 100;
};

/* ═══════════════════════════════════════════════════════════
   Main Component
══════════════════════════════════════════════════════════ */
const UserProfile = () => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const dispatch  = useDispatch();
  const { isAuthenticated, user } = useSelector((s) => s.auth);

  const [activeSection, setActiveSection]     = useState('profile');
  const [orders, setOrders]                   = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [trackLoading, setTrackLoading]       = useState(false);
  const [trackingNumber, setTrackingNumber]   = useState('');
  const [trackedOrder, setTrackedOrder]       = useState(null);
  const [trackError, setTrackError]           = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [showCurrentPwd, setShowCurrentPwd]   = useState(false);
  const [showNewPwd, setShowNewPwd]           = useState(false);
  const [showConfirmPwd, setShowConfirmPwd]   = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword]         = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [sidebarOpen, setSidebarOpen]         = useState(false);
  const [isMobile, setIsMobile]               = useState(window.innerWidth <= 768);
  /* ── Order filter / search / pagination ── */
  const [orderSearch, setOrderSearch]         = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('All');
  const [orderPage, setOrderPage]             = useState(1);
  const ORDERS_PER_PAGE = 10;
  const ORDER_STATUSES = ['All', 'Pending', 'Processing', 'Shipped', 'Delivered', 'Cancelled', 'Return'];

  /* ── Mobile resize listener ── */
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /* ── Close sidebar on navigate (mobile) ── */
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  /* ── Route sync ── */
  useEffect(() => {
    const path = location.pathname.split('/')[1];
    const found = MENU.find((m) => m.path === `/${path}`);
    const section = found?.id || 'profile';
    setActiveSection(section);
    if (section === 'orders') fetchOrders();
    if (section === 'track') { setTrackedOrder(null); setTrackingNumber(''); setTrackError(null); }
  }, [location.pathname]);

  /* ── Handlers ── */
  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await authService.getUserOrders();
      const arr = Array.isArray(res.orders) ? res.orders : [];
      setOrders(Array.from(new Map(arr.map((o) => [o._id, o])).values()));
    } catch (err) {
      toast.error(err.message || 'Failed to fetch orders');
    } finally { setLoading(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('customer');
    dispatch(logout());
    navigate('/login');
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData(e.target);
      const data = {
        firstname:   fd.get('firstname')?.trim(),
        lastname:    fd.get('lastname')?.trim(),
        phoneNumber: fd.get('phoneNumber')?.trim(),
      };
      if (!data.firstname || !data.lastname || !data.phoneNumber) throw new Error('All fields are required');
      if (!/^\d{10}$/.test(data.phoneNumber)) throw new Error('Invalid phone number (10 digits required)');
      const response = await authService.updateProfile(data);
      if (!response) throw new Error('No response from server');
      dispatch(updateUser(response));
      toast.success('Profile updated successfully!');
    } catch (err) {
      toast.error(err.message || 'Failed to update profile');
    } finally { setLoading(false); }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (newPassword !== confirmNewPassword) throw new Error('Passwords do not match');
      if (newPassword.length < 3) throw new Error('Password must be at least 3 characters');
      await authService.updatePassword({ currentPassword, newPassword, confirmPassword: confirmNewPassword });
      toast.success('Password changed successfully!');
      setCurrentPassword(''); setNewPassword(''); setConfirmNewPassword('');
    } catch (err) {
      toast.error(err.message || 'Failed to change password');
    } finally { setLoading(false); }
  };

  const handleTrackOrder = async (e) => {
    e.preventDefault();
    if (!trackingNumber.trim()) { setTrackError('Tracking number is required'); return; }
    setTrackLoading(true); setTrackError(null); setTrackedOrder(null);
    try {
      const token = JSON.parse(localStorage.getItem('customer'))?.token;
      const res = await axios.post(
        `${base_url}user/trackorder`,
        { trackingNumber: trackingNumber.trim() },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (res.data) { setTrackedOrder(res.data); toast.success('Order found!'); }
      else setTrackError('No order found with this tracking number');
    } catch (err) {
      setTrackError(err.response?.data?.message || 'Failed to track order');
    } finally { setTrackLoading(false); }
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    try {
      await axios.delete(`${base_url}user/${user._id}`, {
        headers: { Authorization: `Bearer ${JSON.parse(localStorage.getItem('customer'))?.token}` },
      });
      localStorage.removeItem('customer');
      dispatch(logout());
      toast.success('Account deleted successfully.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete account');
    } finally { setLoading(false); setOpenDeleteDialog(false); }
  };

  const userData = {
    firstname:   user?.firstname || '',
    lastname:    user?.lastname  || '',
    email:       user?.email     || '',
    phoneNumber: user?.phoneNumber || '',
    createdAt:   user?.createdAt || '',
    lastLogin:   user?.lastLogin || '',
  };

  /* ═══════════════════════════════════════════════════════
     Section renderers
  ════════════════════════════════════════════════════════ */
  const renderProfile = () => (
    <div>
      {/* Avatar banner */}
      <div style={s.avatarBanner}>
        <div style={s.avatar}>
          {userData.firstname?.[0]?.toUpperCase() || 'U'}
        </div>
        <div>
          <p style={s.avatarName}>{userData.firstname} {userData.lastname}</p>
          <p style={s.avatarEmail}><FaEnvelope style={{ marginRight: 6 }} />{userData.email}</p>
        </div>
      </div>

      <h2 style={s.sectionTitle}>Edit Profile</h2>
      <form onSubmit={handleProfileUpdate} style={s.form}>
        <div style={s.formRow}>
          <div style={s.formGroup}>
            <label style={s.label}>First Name</label>
            <input name="firstname" defaultValue={userData.firstname} required style={s.input} placeholder="First Name" />
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Last Name</label>
            <input name="lastname" defaultValue={userData.lastname} required style={s.input} placeholder="Last Name" />
          </div>
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Email Address</label>
          <div style={s.inputIcon}>
            <FaEnvelope style={s.inputIconIcon} />
            <input value={userData.email} disabled style={{ ...s.input, paddingLeft: 36, backgroundColor: '#f5f5f7', color: '#999', cursor: 'not-allowed' }} />
          </div>
          <p style={s.hint}>Email address cannot be changed.</p>
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Phone Number</label>
          <div style={s.inputIcon}>
            <FaPhone style={s.inputIconIcon} />
            <input name="phoneNumber" defaultValue={userData.phoneNumber} required type="tel" disabled
              pattern="[0-9]{10}" style={{ ...s.input, paddingLeft: 36 }} placeholder="10-digit number" />
          </div>
          <p style={s.hint}>Phone number cannot be changed.</p>
        </div>
        <button type="submit" style={{ ...s.primaryBtn, width: 'auto', marginTop: 8 }} disabled={loading}>
          {loading ? <span style={s.spinner} /> : <><FaSave style={{ marginRight: 6 }} />Save Changes</>}
        </button>
      </form>
    </div>
  );

  const renderOrders = () => {
    /* ── Filter + Search ── */
    const filtered = orders.filter((o) => {
      const matchStatus = orderStatusFilter === 'All' || o.orderStatus === orderStatusFilter;
      const q = orderSearch.toLowerCase();
      const matchSearch = !q ||
        o.trackingNumber?.toLowerCase().includes(q) ||
        o.orderItems?.some(i => i.product?.title?.toLowerCase().includes(q));
      return matchStatus && matchSearch;
    });
    /* ── Pagination ── */
    const totalPages = Math.max(1, Math.ceil(filtered.length / ORDERS_PER_PAGE));
    const safePage   = Math.min(orderPage, totalPages);
    const paginated  = filtered.slice((safePage - 1) * ORDERS_PER_PAGE, safePage * ORDERS_PER_PAGE);

    return (
      <div>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <h2 style={s.sectionTitle}>My Orders</h2>
          <span style={s.countBadge}>{orders.length} total</span>
        </div>

        {/* Search */}
        <div style={s.orderSearchWrap}>
          <FaSearch style={{ position: 'absolute', left: 12, color: '#bbb', fontSize: 13 }} />
          <input
            value={orderSearch}
            onChange={(e) => { setOrderSearch(e.target.value); setOrderPage(1); }}
            placeholder="Search by tracking no. or product name…"
            style={s.orderSearchInput}
          />
          {orderSearch && (
            <button onClick={() => { setOrderSearch(''); setOrderPage(1); }} style={s.clearSearch}>✕</button>
          )}
        </div>

        {/* Status filter tabs */}
        <div style={s.filterTabs}>
          {ORDER_STATUSES.map((st) => {
            const active = orderStatusFilter === st;
            const cfg = statusStyle(st);
            return (
              <button
                key={st}
                onClick={() => { setOrderStatusFilter(st); setOrderPage(1); }}
                style={{
                  ...s.filterTab,
                  backgroundColor: active ? (st === 'All' ? '#232f3e' : cfg.bg) : '#f5f5f7',
                  color: active ? (st === 'All' ? '#fff' : cfg.color) : '#666',
                  fontWeight: active ? 700 : 500,
                  borderColor: active ? (st === 'All' ? '#232f3e' : cfg.color) : 'transparent',
                }}
              >
                {st}
              </button>
            );
          })}
        </div>

        {/* Results */}
        {loading ? (
          <div style={s.centered}><div style={s.bigSpinner} /></div>
        ) : paginated.length === 0 ? (
          <div style={s.emptyState}>
            <BsFillCartCheckFill size={48} color="#e0e0e0" />
            <p style={s.emptyText}>{orders.length === 0 ? 'No orders yet' : 'No orders match your filters'}</p>
            {orders.length === 0 && <Link to="/" style={{ ...s.primaryBtn, width: 'auto' }}>Start Shopping</Link>}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {paginated.map((order) => {
              const st = statusStyle(order.orderStatus);
              const imgUrl = order.orderItems[0]?.product?.images?.[0]?.url;
              return (
                <div key={order._id} style={s.orderCard}>
                  <div style={s.orderTop}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {imgUrl && (
                        <div style={s.orderThumb}>
                          <img src={`${base_url}${imgUrl}`} alt="product" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                      )}
                      <div>
                        <p style={s.trackCode}>{order.trackingNumber || 'N/A'}</p>
<p style={s.orderDate}>
  {new Date(order.createdAt).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })}
</p>                      </div>
                    </div>
                    <span style={{ ...s.statusPill, backgroundColor: st.bg, color: st.color }}>{order.orderStatus}</span>
                  </div>
                  <div style={s.orderMeta}>
                    <div style={s.metaItem}><span style={s.metaLabel}>Items</span><span style={s.metaValue}>{order.orderItems.reduce((t, i) => t + i.quantity, 0)}</span></div>
                    <div style={s.metaItem}><span style={s.metaLabel}>Total</span><span style={{ ...s.metaValue, color: '#ff6701', fontWeight: 700 }}>₹{order.totalPrice?.toFixed(2)}</span></div>
                    <div style={s.metaItem}>
                      <span style={s.metaLabel}>Payment</span>
                      <span style={s.metaValue}>
                        {order.paymentInfo?.method === "COD" ? "COD" : "Online"}
                      </span>
                    </div>                  </div>
                  <div style={{ padding: '10px 16px', borderTop: '1px solid #f5f5f7', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <Link to={`/order/${order._id}`} style={s.viewBtn}>View Details <FaChevronRight size={11} style={{ marginLeft: 4 }} /></Link>
                    <span style={{ fontSize: 11, color: '#bbb' }}>{order.orderItems.length} item{order.orderItems.length > 1 ? 's' : ''}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={s.pagination}>
            <button
              style={{ ...s.pageBtn, opacity: safePage === 1 ? 0.4 : 1 }}
              onClick={() => setOrderPage(p => Math.max(1, p - 1))}
              disabled={safePage === 1}
            >← Prev</button>
            <div style={s.pageNumbers}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(pg => (
                <button
                  key={pg}
                  onClick={() => setOrderPage(pg)}
                  style={{
                    ...s.pageNum,
                    backgroundColor: pg === safePage ? '#ff6701' : 'transparent',
                    color: pg === safePage ? '#fff' : '#555',
                    fontWeight: pg === safePage ? 700 : 400,
                  }}
                >{pg}</button>
              ))}
            </div>
            <button
              style={{ ...s.pageBtn, opacity: safePage === totalPages ? 0.4 : 1 }}
              onClick={() => setOrderPage(p => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
            >Next →</button>
          </div>
        )}

        {/* Results count */}
        {filtered.length > 0 && (
          <p style={{ fontSize: 12, color: '#aaa', textAlign: 'center', marginTop: 12 }}>
            Showing {(safePage - 1) * ORDERS_PER_PAGE + 1}–{Math.min(safePage * ORDERS_PER_PAGE, filtered.length)} of {filtered.length} orders
          </p>
        )}
      </div>
    );
  };

  const renderTrack = () => (
    <div>
      <h2 style={s.sectionTitle}>Track Your Order</h2>
      <p style={s.subText}>Enter your tracking number to get real-time order updates.</p>

      <form onSubmit={handleTrackOrder} style={{ ...s.form, maxWidth: 520 }}>
        <div style={s.formGroup}>
          <label style={s.label}>Tracking Number</label>
          <div style={s.inputIcon}>
            <FaSearch style={s.inputIconIcon} />
            <input
              value={trackingNumber}
              onChange={(e) => { setTrackingNumber(e.target.value); setTrackError(null); }}
              style={{ ...s.input, paddingLeft: 36, borderColor: trackError ? '#ef4444' : '#e0e0e0' }}
              placeholder="e.g. TRK-202500123"
            />
          </div>
          {trackError && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{trackError}</p>}
        </div>
        <button type="submit" style={s.primaryBtn} disabled={trackLoading}>
          {trackLoading ? <><span style={s.spinner} /> Searching…</> : <><FaTruck style={{ marginRight: 6 }} />Track Order</>}
        </button>
      </form>

      {/* ── Result card ── */}
      {trackedOrder && (
        <div style={{ marginTop: 28 }}>
          <div style={s.trackedCard}>

            {/* Header row */}
            <div style={s.trackedHeader}>
              <FaCheckCircle color="#10b981" size={18} />
              <span style={{ fontWeight: 700, color: '#1c1c1b', marginLeft: 8 }}>Order Found</span>
              <span style={{ ...s.statusPill, ...statusStyle(trackedOrder.orderStatus), marginLeft: 'auto' }}>
                {trackedOrder.orderStatus}
              </span>
            </div>

            <div style={{ padding: '16px 18px' }}>
              {/* Tracking # and date */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                <p style={s.trackedTrack}>#{trackedOrder.trackingNumber}</p>
                {trackedOrder.createdAt && (
                  <span style={{ fontSize: 12, color: '#999' }}>
                    Ordered: {new Date(trackedOrder.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                )}
              </div>

              {/* Address */}
              <div style={s.trackedRow}>
                <FaMapMarkerAlt color="#ff6701" style={{ flexShrink: 0, marginTop: 2 }} />
                <span style={{ fontSize: 13, color: '#555' }}>
                  {trackedOrder.shippingInfo?.firstName && `${trackedOrder.shippingInfo.firstName} ${trackedOrder.shippingInfo.lastName}, `}
                  {trackedOrder.shippingInfo?.address}, {trackedOrder.shippingInfo?.city}, {trackedOrder.shippingInfo?.state}
                  {trackedOrder.shippingInfo?.landmark && ` — Near: ${trackedOrder.shippingInfo.landmark}`}
                </span>
              </div>

              {/* ── Visual Progress Stepper ── */}
              <div style={{ marginTop: 22, marginBottom: 8 }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 16 }}>Order Status</p>

                {trackedOrder.orderStatus === 'Cancelled' ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', backgroundColor: '#fef2f2', borderRadius: 10, border: '1px solid #fecaca' }}>
                    <FaTimesCircle color="#ef4444" size={20} />
                    <div>
                      <p style={{ fontWeight: 700, color: '#ef4444', margin: 0, fontSize: 14 }}>Order Cancelled</p>
                      {trackedOrder.cancellationReason && (
                        <p style={{ fontSize: 12, color: '#b91c1c', margin: 0, marginTop: 2 }}>Reason: {trackedOrder.cancellationReason}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={s.stepperWrap}>
                    {/* Background line */}
                    <div style={s.stepperBgLine} />
                    {/* Progress fill */}
                    <div style={{ ...s.stepperProgressLine, width: `${calcProgress(trackedOrder.orderStatus)}%` }} />
                    {/* Steps */}
                    {TRACK_STAGES.map((stage, idx) => {
                      const completed = idx <= getActiveStepIndex(trackedOrder.orderStatus);
                      const active    = idx === getActiveStepIndex(trackedOrder.orderStatus);
                      return (
                        <div key={stage} style={s.stepItem}>
                          <div style={{
                            ...s.stepCircle,
                            backgroundColor: completed ? (active ? '#ff6701' : '#10b981') : '#e0e0e0',
                            boxShadow: active ? '0 0 0 4px rgba(255,103,1,0.2)' : 'none',
                            transform: active ? 'scale(1.15)' : 'scale(1)',
                          }}>
                            <span style={{ fontSize: 16 }}>{TRACK_STAGE_ICONS[stage]}</span>
                          </div>
                          <p style={{ ...s.stepLabel, color: completed ? '#232f3e' : '#aaa', fontWeight: active ? 700 : 500 }}>
                            {stage}
                          </p>
                          {active && <p style={s.stepActive}>Current</p>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Items list */}
              <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 14 }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Items</p>
                {trackedOrder.orderItems?.map((item, i) => (
                  <div key={i} style={s.trackedItem}>
                    <span style={{ fontSize: 13, color: '#333', flex: 1 }}>{item.product?.title}</span>
                    <span style={{ fontSize: 13, color: '#777' }}>×{item.quantity}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#232f3e' }}>₹{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, paddingTop: 10, borderTop: '1px solid #f0f0f0' }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#ff6701' }}>
                  Total: ₹{trackedOrder.orderItems?.reduce((a, i) => a + i.price * i.quantity, 0)?.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderPassword = () => (
    <div>
      <h2 style={s.sectionTitle}>Change Password</h2>
      <p style={s.subText}>Keep your account secure with a strong password.</p>
      <form onSubmit={handlePasswordChange} style={{ ...s.form, maxWidth: 440 }}>
        {[
          { label: 'Current Password', val: currentPassword, set: setCurrentPassword, show: showCurrentPwd, setShow: setShowCurrentPwd },
          { label: 'New Password',     val: newPassword,     set: setNewPassword,     show: showNewPwd,     setShow: setShowNewPwd },
          { label: 'Confirm New Password', val: confirmNewPassword, set: setConfirmNewPassword, show: showConfirmPwd, setShow: setShowConfirmPwd },
        ].map(({ label, val, set, show, setShow }) => (
          <div key={label} style={s.formGroup}>
            <label style={s.label}>{label}</label>
            <div style={s.inputIcon}>
              <FaLock style={s.inputIconIcon} />
              <input
                type={show ? 'text' : 'password'}
                value={val}
                onChange={(e) => set(e.target.value)}
                required
                style={{ ...s.input, paddingLeft: 36, paddingRight: 40 }}
                placeholder={label}
              />
              <button type="button" onClick={() => setShow(!show)} style={s.eyeBtn}>
                {show ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>
        ))}
        <div style={{ textAlign: 'right', marginBottom: 12 }}>
          <Link to="/forget-password" style={{ fontSize: 13, color: '#ff6701', fontWeight: 500 }}>Forgot Password?</Link>
        </div>
        <button type="submit" style={s.primaryBtn} disabled={loading}>
          {loading ? <><span style={s.spinner} /> Updating…</> : <><FaLock style={{ marginRight: 6 }} />Update Password</>}
        </button>
      </form>
    </div>
  );

  const renderAccount = () => (
    <div>
      <h2 style={s.sectionTitle}>Account Settings</h2>
      <p style={s.subText}>Manage your account preferences.</p>

      {/* Info cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 28 }}>
        {[
          { icon: <FaEnvelope color="#ff6701" />, label: 'Email Address',   val: userData.email },
          { icon: <FaClock color="#ff6701" />,    label: 'Member Since',    val: userData.createdAt ? new Date(userData.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—' },
          { icon: <FaClock color="#ff6701" />,    label: 'Last Login',      val: userData.lastLogin ? new Date(userData.lastLogin).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—' },
        ].map(({ icon, label, val }) => (
          <div key={label} style={s.infoCard}>
            <span style={s.infoCardIcon}>{icon}</span>
            <div>
              <p style={{ fontSize: 11, color: '#999', margin: 0 }}>{label}</p>
              <p style={{ fontSize: 14, fontWeight: 600, color: '#232f3e', margin: 0 }}>{val}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Danger zone */}
      <div style={s.dangerZone}>
        <div style={{ marginBottom: 12 }}>
          <p style={{ fontWeight: 700, color: '#ef4444', fontSize: 14, margin: 0 }}>⚠ Danger Zone</p>
          <p style={{ fontSize: 13, color: '#888', marginTop: 4 }}>Deleting your account is permanent and cannot be undone.</p>
        </div>
        <button style={s.dangerBtn} onClick={() => setOpenDeleteDialog(true)} disabled={loading}>
          <FaTrashAlt style={{ marginRight: 6 }} /> Delete My Account
        </button>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':  return renderProfile();
      case 'orders':   return renderOrders();
      case 'track':    return renderTrack();
      case 'password': return renderPassword();
      case 'account':  return renderAccount();
      default:         return null;
    }
  };

  /* ═══════════════════════════════════════════════════════
     Render
  ════════════════════════════════════════════════════════ */
  return (
    <div style={s.page}>
      <div style={s.container}>

        {/* Mobile hamburger — only visible on mobile */}
        {isMobile && (
          <button style={s.hamburger} onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? '✕ Close' : '☰ Menu'}
          </button>
        )}

        <div style={{ ...s.layout, gridTemplateColumns: isMobile ? '1fr' : '240px 1fr' }}>
          {/* ── Sidebar ── */}
          {/* On desktop always show; on mobile show as overlay when sidebarOpen */}
          {(!isMobile || sidebarOpen) && (
          <aside style={{
            ...s.sidebar,
            ...(isMobile ? {
              position: 'fixed', top: 0, left: 0, bottom: 0,
              width: 260, zIndex: 1000,
              boxShadow: '4px 0 24px rgba(0,0,0,0.18)',
              overflowY: 'auto',
            } : {}),
          }}>
            {/* User pill */}
            <div style={s.sidebarUser}>
              <div style={s.sidebarAvatar}>
                {userData.firstname?.[0]?.toUpperCase() || 'U'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={s.sidebarName}>{userData.firstname} {userData.lastname}</p>
                <p style={s.sidebarEmail}>{userData.email}</p>
              </div>
            </div>

            {/* Nav */}
            <nav style={{ flex: 1 }}>
              {MENU.map((item) => {
                const active = activeSection === item.id;
                return (
                  <Link
                    key={item.id}
                    to={item.path}
                    onClick={() => { setActiveSection(item.id); setSidebarOpen(false); }}
                    style={{ ...s.navItem, ...(active ? s.navItemActive : {}) }}
                  >
                    <span style={{ ...s.navIcon, color: active ? '#ff6701' : '#888' }}>{item.icon}</span>
                    <span style={{ flex: 1 }}>{item.label}</span>
                    {active && <FaChevronRight size={11} color="#ff6701" />}
                  </Link>
                );
              })}
            </nav>

            {/* Logout */}
            <button style={s.logoutBtn} onClick={handleLogout}>
              <FaSignOutAlt style={{ marginRight: 8 }} /> Logout
            </button>
          </aside>
          )}

          {/* Mobile backdrop */}
          {isMobile && sidebarOpen && (
            <div
              style={s.backdrop}
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* ── Main content ── */}
          <main style={{ ...s.main, padding: isMobile ? '20px 14px' : '28px 24px' }}>
            {renderContent()}
          </main>
        </div>
      </div>

      {/* ── Delete Account Dialog ── */}
      {openDeleteDialog && (
        <div style={s.overlay} onClick={() => !loading && setOpenDeleteDialog(false)}>
          <div style={s.dialog} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <FaTimesCircle color="#ef4444" size={22} />
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1c1c1b', margin: 0 }}>Delete Account?</h2>
            </div>
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 14px', marginBottom: 20 }}>
              <p style={{ fontSize: 13, color: '#b91c1c', margin: 0, lineHeight: 1.6 }}>
                ⚠ This action is <strong>permanent</strong>. All your data — orders, addresses, and profile info — will be permanently removed and cannot be recovered.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button style={s.ghostBtn} onClick={() => setOpenDeleteDialog(false)} disabled={loading}>Keep Account</button>
              <button style={{ ...s.dangerBtn, flex: 1, opacity: loading ? 0.6 : 1 }} onClick={handleDeleteAccount} disabled={loading}>
                {loading ? 'Deleting…' : <><FaTrashAlt style={{ marginRight: 6 }} />Yes, Delete</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────── Styles ──────────────────────────────── */
const s = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f7',
    fontFamily: '"Rubik", "Poppins", sans-serif',
    paddingBottom: 60,
  },
  container: { maxWidth: 1100, margin: '0 auto', padding: '16px' },
  hamburger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: '#fff',
    border: '1.5px solid #e0e0e0',
    borderRadius: 8,
    padding: '8px 16px',
    fontFamily: 'inherit',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    marginBottom: 14,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  layout: {
    display: 'grid',
    gap: 20,
    alignItems: 'start',
  },
  backdrop: {
    position: 'fixed', inset: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    zIndex: 999,
  },

  /* ─ Sidebar ─ */
  sidebar: {
    background: '#fff',
    borderRadius: 14,
    boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    position: 'sticky',
    top: 16,
  },
  sidebarUser: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '18px 16px',
    borderBottom: '1px solid #f0f0f0',
    background: 'linear-gradient(135deg,#232f3e 0%,#3b4149 100%)',
  },
  sidebarAvatar: {
    width: 42, height: 42, borderRadius: '50%',
    background: 'linear-gradient(135deg,#ff6701,#febd69)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 800, fontSize: 18, color: '#fff', flexShrink: 0,
  },
  sidebarName: { fontSize: 14, fontWeight: 700, color: '#fff', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  sidebarEmail: { fontSize: 11, color: 'rgba(255,255,255,0.65)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  navItem: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '12px 16px',
    fontSize: 13, fontWeight: 500, color: '#444',
    textDecoration: 'none',
    transition: 'all 0.2s',
    borderLeft: '3px solid transparent',
  },
  navItemActive: {
    backgroundColor: '#fff8f4',
    color: '#ff6701',
    fontWeight: 700,
    borderLeft: '3px solid #ff6701',
  },
  navIcon: { fontSize: 15, flexShrink: 0 },
  logoutBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: 12, padding: '10px 16px',
    border: '1.5px solid #e0e0e0', borderRadius: 8,
    background: 'none', color: '#ef4444',
    fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
  },

  /* ─ Main ─ */
  main: {
    background: '#fff',
    borderRadius: 14,
    padding: '28px 24px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
    minHeight: 400,
  },
  sectionTitle: { fontSize: 20, fontWeight: 700, color: '#1c1c1b', margin: '0 0 4px 0' },
  subText: { fontSize: 13, color: '#888', marginBottom: 22, marginTop: 4 },

  /* ─ Avatar banner ─ */
  avatarBanner: {
    display: 'flex', alignItems: 'center', gap: 16,
    padding: '16px 20px',
    background: 'linear-gradient(135deg,#232f3e 0%,#3b4149 100%)',
    borderRadius: 12,
    marginBottom: 24,
  },
  avatar: {
    width: 56, height: 56, borderRadius: '50%',
    background: 'linear-gradient(135deg,#ff6701,#febd69)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 800, fontSize: 22, color: '#fff', flexShrink: 0,
  },
  avatarName:  { fontSize: 16, fontWeight: 700, color: '#fff', margin: 0 },
  avatarEmail: { fontSize: 12, color: 'rgba(255,255,255,0.7)', margin: 0, display: 'flex', alignItems: 'center', marginTop: 3 },

  /* ─ Forms ─ */
  form: { display: 'flex', flexDirection: 'column', gap: 0 },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 },
  formGroup: { marginBottom: 14, display: 'flex', flexDirection: 'column' },
  label: { fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 },
  input: {
    width: '100%', padding: '11px 14px',
    border: '1.5px solid #e0e0e0', borderRadius: 10,
    fontSize: 14, fontFamily: 'inherit', color: '#333',
    outline: 'none', boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  inputIcon: { position: 'relative', display: 'flex', alignItems: 'center' },
  inputIconIcon: { position: 'absolute', left: 12, color: '#bbb', fontSize: 13 },
  eyeBtn: {
    position: 'absolute', right: 12, background: 'none',
    border: 'none', cursor: 'pointer', color: '#aaa', fontSize: 14,
    display: 'flex', alignItems: 'center',
  },
  hint: { fontSize: 11, color: '#aaa', marginTop: 4 },

  /* ─ Buttons ─ */
  primaryBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: '12px 24px', width: '100%',
    backgroundColor: '#232f3e', color: '#fff',
    border: 'none', borderRadius: 10,
    fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
    textDecoration: 'none', transition: 'background 0.2s',
  },
  dangerBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: '11px 20px',
    backgroundColor: '#fff', color: '#ef4444',
    border: '1.5px solid #ef4444', borderRadius: 10,
    fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
    transition: 'all 0.2s',
  },
  ghostBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: '11px 20px', flex: 1,
    backgroundColor: '#f5f5f7', color: '#555',
    border: '1.5px solid #e0e0e0', borderRadius: 10,
    fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
  },
  viewBtn: {
    display: 'inline-flex', alignItems: 'center',
    fontSize: 13, fontWeight: 600, color: '#0127ffff',
    textDecoration: 'none',
  },

  /* ─ Spinners ─ */
  spinner: {
    display: 'inline-block', width: 14, height: 14,
    border: '2px solid rgba(255,255,255,0.3)',
    borderTop: '2px solid #fff',
    borderRadius: '50%', marginRight: 8,
    animation: 'spin 0.7s linear infinite',
  },
  bigSpinner: {
    width: 40, height: 40,
    border: '3px solid #f0f0f0', borderTop: '3px solid #ff6701',
    borderRadius: '50%', animation: 'spin 0.8s linear infinite',
  },
  centered: { display: 'flex', justifyContent: 'center', padding: '40px 0' },

  /* ─ Order cards ─ */
  countBadge: {
    fontSize: 12, fontWeight: 700, color: '#ff6701',
    background: '#fff3e8', padding: '3px 10px', borderRadius: 20,
  },
  orderCard: {
    border: '1px solid #f0f0f0', borderRadius: 12,
    overflow: 'hidden', transition: 'box-shadow 0.2s',
    backgroundColor: '#fafafa',
  },
  orderTop: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 16px', borderBottom: '1px solid #f0f0f0',
    backgroundColor: '#fff',
  },
  orderThumb: {
    width: 44, height: 44, borderRadius: 8,
    overflow: 'hidden', flexShrink: 0,
    border: '1px solid #eee', backgroundColor: '#f5f5f7',
  },
  trackCode: { fontSize: 13, fontWeight: 700, color: '#ff6701', fontFamily: 'monospace', margin: 0 },
  orderDate: { fontSize: 11, color: '#999', margin: 0, marginTop: 2 },
  statusPill: {
    display: 'inline-flex', alignItems: 'center',
    padding: '3px 10px', borderRadius: 20,
    fontWeight: 700, fontSize: 11,
  },
  orderMeta: {
    display: 'flex', gap: 0,
    padding: '10px 16px',
    backgroundColor: '#fff',
  },
  metaItem: {
    display: 'flex', flexDirection: 'column', flex: 1,
    borderRight: '1px solid #f0f0f0',
    paddingRight: 12, paddingLeft: 8,
    ':last-child': { borderRight: 'none' },
  },
  metaLabel: { fontSize: 11, color: '#aaa', marginBottom: 2 },
  metaValue: { fontSize: 13, fontWeight: 600, color: '#333' },

  /* ─ Empty ─ */
  emptyState: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 12, padding: '48px 0', color: '#bbb',
  },
  emptyText: { fontSize: 14, color: '#aaa', margin: 0 },

  /* ─ Track result ─ */
  trackedCard: {
    border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden',
  },
  trackedHeader: {
    display: 'flex', alignItems: 'center',
    padding: '12px 18px', borderBottom: '1px solid #f0f0f0',
    backgroundColor: '#f9fafb',
  },
  trackedTrack: { fontSize: 15, fontWeight: 700, color: '#ff6701', fontFamily: 'monospace', margin: 0 },
  trackedRow: { display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8 },
  trackedItem: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '7px 0', borderBottom: '1px solid #f5f5f7',
  },
  /* ─ Stepper ─ */
  stepperWrap: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    position: 'relative',
    padding: '20px 10px 10px',
    backgroundColor: '#f9fafb',
    borderRadius: 12,
    overflowX: 'auto',
  },
  stepperBgLine: {
    position: 'absolute', top: 36, left: 40, right: 40,
    height: 4, backgroundColor: '#e0e0e0', zIndex: 1,
  },
  stepperProgressLine: {
    position: 'absolute', top: 36, left: 40,
    height: 4, backgroundColor: '#ff6701', zIndex: 2,
    transition: 'width 0.4s ease',
  },
  stepItem: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    zIndex: 3, minWidth: 64,
  },
  stepCircle: {
    width: 44, height: 44, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.3s ease', marginBottom: 8,
  },
  stepLabel: {
    fontSize: 11, textAlign: 'center', margin: 0,
    lineHeight: 1.3, maxWidth: 64,
  },
  stepActive: {
    fontSize: 10, color: '#ff6701', fontWeight: 700,
    margin: '2px 0 0 0',
  },

  /* ─ Account ─ */
  infoCard: {
    display: 'flex', alignItems: 'center', gap: 14,
    padding: '14px 16px', borderRadius: 10,
    border: '1px solid #f0f0f0', backgroundColor: '#fafafa',
  },
  infoCardIcon: {
    width: 36, height: 36, borderRadius: 8,
    backgroundColor: '#fff3e8',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  dangerZone: {
    border: '1.5px solid #fecaca', borderRadius: 12,
    padding: '16px 18px', backgroundColor: '#fff5f5',
  },

  /* ─ Dialog ─ */
  overlay: {
    position: 'fixed', inset: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999, padding: 16,
  },
  dialog: {
    backgroundColor: '#fff', borderRadius: 16,
    padding: '28px 24px', width: '100%', maxWidth: 420,
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    fontFamily: '"Rubik", sans-serif',
  },

  /* ─ Order search ─ */
  orderSearchWrap: {
    position: 'relative', display: 'flex', alignItems: 'center',
    marginBottom: 14,
  },
  orderSearchInput: {
    width: '100%', padding: '10px 36px 10px 36px',
    border: '1.5px solid #e0e0e0', borderRadius: 10,
    fontSize: 13, fontFamily: 'inherit', color: '#333',
    outline: 'none', boxSizing: 'border-box',
    backgroundColor: '#fafafa',
  },
  clearSearch: {
    position: 'absolute', right: 10, background: 'none', border: 'none',
    cursor: 'pointer', color: '#aaa', fontSize: 14, padding: 2,
  },

  /* ─ Filter tabs ─ */
  filterTabs: {
    display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16,
  },
  filterTab: {
    padding: '5px 12px', borderRadius: 20, fontSize: 12,
    border: '1.5px solid transparent', cursor: 'pointer',
    fontFamily: 'inherit', transition: 'all 0.2s',
  },

  /* ─ Pagination ─ */
  pagination: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 8, marginTop: 20, flexWrap: 'wrap',
  },
  pageBtn: {
    padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
    border: '1.5px solid #e0e0e0', background: '#fff', color: '#444',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  pageNumbers: { display: 'flex', gap: 4 },
  pageNum: {
    width: 32, height: 32, borderRadius: 8,
    border: 'none', fontSize: 13, cursor: 'pointer',
    fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
};

export default UserProfile;